import { useState, useRef } from 'react';
import { useSubcontractors } from '../../context/SubcontractorContext';
import SubOverviewTab from './tabs/SubOverviewTab';
import SubActiveJobsTab from './tabs/SubActiveJobsTab';
import SubHistoryTab from './tabs/SubHistoryTab';
import SubFinancialsTab from './tabs/SubFinancialsTab';
import SubcontractorFormModal from './modals/SubcontractorFormModal';

const STATUS_STYLES = {
  active: {
    bg: 'bg-[#10B981]/10',
    text: 'text-[#10B981]',
    border: 'border-[#10B981]/30',
  },
  inactive: {
    bg: 'bg-[#F59E0B]/10',
    text: 'text-[#F59E0B]',
    border: 'border-[#F59E0B]/30',
  },
  blacklisted: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
  },
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'active', label: 'Active Jobs' },
  { id: 'history', label: 'History' },
  { id: 'financials', label: 'Financials' },
];

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

export default function SubcontractorDetailPanel({ sub, onClose }) {
  const { deleteSubcontractor } = useSubcontractors();
  const [activeTab, setActiveTab] = useState('overview');
  const [editModal, setEditModal] = useState(false);
  const prevSubIdRef = useRef(sub?.id);

  // Reset tab to 'overview' when the selected subcontractor changes.
  // We do this synchronously during render (not in an effect) to avoid
  // the react-hooks/set-state-in-effect lint rule.
  if (prevSubIdRef.current !== sub?.id) {
    prevSubIdRef.current = sub?.id;
    if (activeTab !== 'overview') {
      setActiveTab('overview');
    }
  }

  if (!sub) {
    return (
      <div className="flex-1 flex items-center justify-center text-[#64748B] dark:text-[#4A6380] text-[var(--wm-text-body)]">
        Select a subcontractor to view details
      </div>
    );
  }

  const statusStyle = STATUS_STYLES[sub.status] ?? STATUS_STYLES.inactive;

  function handleDelete() {
    if (
      window.confirm(
        'Permanently delete this subcontractor? This will also delete all associated jobs, history, and financial records. This cannot be undone.'
      )
    ) {
      deleteSubcontractor(sub.id);
      onClose();
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-[#1E3048] flex-shrink-0">
        {/* Left side */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-[var(--wm-text-body)] flex-shrink-0"
            style={{ backgroundColor: sub.avatarColor ?? '#2E8BF0' }}
          >
            {getInitials(sub.name)}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate">
                {sub.name}
              </span>
              <span
                className={`text-[var(--wm-text-ui)] px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
              >
                {sub.status}
              </span>
            </div>
            {sub.company && (
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] truncate">{sub.company}</p>
            )}
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setEditModal(true)}
            aria-label={`Edit ${sub.name}`}
            className="text-[var(--wm-text-ui)] px-3 py-1.5 border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] hover:border-[#2E8BF0] rounded-lg transition-colors"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={handleDelete}
            aria-label={`Delete ${sub.name}`}
            className="text-[var(--wm-text-ui)] px-3 py-1.5 border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:text-[#EF4444] hover:border-[#EF4444] rounded-lg transition-colors"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors"
            aria-label="Close panel"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 18 18"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div role="tablist" className="flex border-b border-gray-200 dark:border-[#1E3048] flex-shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-[var(--wm-text-ui)] font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-[#2E8BF0] text-[#2E8BF0]'
                : 'border-transparent text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {activeTab === 'overview' && (
          <SubOverviewTab sub={sub} onEdit={() => setEditModal(true)} />
        )}
        {activeTab === 'active' && <SubActiveJobsTab sub={sub} />}
        {activeTab === 'history' && <SubHistoryTab sub={sub} />}
        {activeTab === 'financials' && <SubFinancialsTab sub={sub} />}
      </div>

      {/* Edit modal */}
      {editModal && (
        <SubcontractorFormModal sub={sub} onClose={() => setEditModal(false)} />
      )}
    </div>
  );
}
